import { useCallback, useEffect, useMemo, useState } from "react";
import { reportsService, groupService } from "@/services";
import type { KametiGroup, ReceiptView } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Button } from "@/components/base/Button";
import { Badge } from "@/components/base/Badge";
import { ErrorState } from "@/components/base/ErrorState";
import { EmptyState } from "@/components/base/EmptyState";
import { PrintLayout } from "@/components/feature/PrintLayout";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR } from "@/utils/format";
import { PAYMENT_MODES, paymentModeLabel } from "@/pages/collection/components/labels";
import { exportReportCSV, csvAmount } from "@/utils/report";

export default function CollectionRegisterPage() {
  const [range, setRange] = useState<DateRange>(() => {
    const now = new Date();
    return { start: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), end: now.toISOString() };
  });
  const [groupId, setGroupId] = useState("");
  const [mode, setMode] = useState("");
  const [staff, setStaff] = useState("");
  const [data, setData] = useState<{ rows: ReceiptView[]; modeTotals: Record<string, number>; grandTotal: number; staff: string[] } | null>(null);
  const [groups, setGroups] = useState<KametiGroup[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, g] = await Promise.all([
        reportsService.getCollectionRegister({ range, groupId, mode, collectedBy: staff }),
        groupService.listGroups(),
      ]);
      setData(r);
      setGroups(g);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the register.");
      setData(null);
    }
  }, [range, groupId, mode, staff]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilters = (groupId ? 1 : 0) + (mode ? 1 : 0) + (staff ? 1 : 0) + (range.start ? 1 : 0);
  const clearFilters = () => {
    setGroupId("");
    setMode("");
    setStaff("");
    setRange({ start: null, end: null });
  };

  const filterSummary = useMemo(() => {
    const parts: string[] = [];
    if (range.start) parts.push(`From ${formatDate(range.start)}`);
    if (range.end) parts.push(`To ${formatDate(range.end)}`);
    if (groupId) parts.push(groups.find((g) => g.id === groupId)?.name ?? "Group");
    if (mode) parts.push(mode);
    if (staff) parts.push(staff);
    return parts.length ? parts.join(" · ") : "All receipts";
  }, [range, groupId, mode, staff, groups]);

  const handleExport = () => {
    if (!data) return;
    exportReportCSV({
      filename: "collection-register",
      reportName: "Collection Register",
      filters: [filterSummary],
      columns: [
        { header: "Receipt", value: (r) => r.receiptNumber },
        { header: "Date", value: (r) => formatDate(r.receivedOn) },
        { header: "Member", value: (r) => r.memberName },
        { header: "Group", value: (r) => r.lines.map((l) => l.groupName).join(", ") },
        { header: "Cycle", value: (r) => r.lines.map((l) => l.cycle).join(", ") },
        { header: "Amount", value: (r) => csvAmount(r.total) },
        { header: "Mode", value: (r) => paymentModeLabel(r.mode) },
        { header: "Reference", value: (r) => r.reference },
        { header: "Collected by", value: (r) => r.collectedBy },
        { header: "Status", value: (r) => (r.reversedAt ? "Reversed" : "Active") },
      ],
      rows: data.rows.map((r) => ({
        receiptNumber: r.receiptNumber,
        receivedOn: r.receivedOn,
        memberName: r.memberName,
        groupName: r.lines.map((l) => l.groupName).join(", "),
        cycle: r.lines.map((l) => l.cycle).join(", "),
        total: r.total,
        mode: r.mode,
        reference: r.reference,
        collectedBy: r.collectedBy,
        reversedAt: r.reversedAt,
      })),
    });
  };

  const activeRows = (data?.rows ?? []).filter((r) => !r.reversedAt);

  return (
    <div>
      <PageHeader
        className="no-print"
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Collection register" isLast />
          </>
        }
        title="Collection register"
        subtitle="Every receipt in the chosen range, with totals by payment mode so cash reconciles physically."
      />

      <div className="no-print mb-3 flex flex-wrap items-center gap-2">
        <DateRangePicker value={range} onChange={setRange} />
        <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All modes</option>
          {PAYMENT_MODES.map((m) => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <select value={staff} onChange={(e) => setStaff(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">All collectors</option>
          {(data?.staff ?? []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {activeFilters > 0 ? (
          <button type="button" onClick={clearFilters} className="cursor-pointer text-xs font-medium text-primary-600 hover:underline">
            Clear filters ({activeFilters})
          </button>
        ) : null}
      </div>

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!data ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : data.rows.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <EmptyState icon="ri-wallet-3-line" title="No receipts match" description="No collections were recorded in this range with the current filters." />
        </div>
      ) : (
        <PrintLayout
          title="Collection Register"
          meta={filterSummary}
          actions={<Button size="sm" variant="outline" icon={<i className="ri-download-2-line" />} onClick={handleExport}>Export</Button>}
        >
          {/* Mode-wise subtotals */}
          <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-md border border-background-200 bg-background-200 sm:grid-cols-3 lg:grid-cols-6">
            {PAYMENT_MODES.map((m) => (
              <div key={m.value} className="bg-background-50 p-3">
                <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">{m.label}</p>
                <p className="tnum mt-1 text-base font-bold text-foreground-900">{formatINR(data.modeTotals[m.value] ?? 0)}</p>
              </div>
            ))}
            <div className="bg-background-100 p-3">
              <p className="text-xs uppercase tracking-wider text-foreground-500 font-medium">Grand total</p>
              <p className="tnum mt-1 text-base font-bold text-primary-700">{formatINR(data.grandTotal)}</p>
            </div>
          </div>

          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-background-100 text-foreground-600">
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Receipt</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Member</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Group / Cycle</th>
                <th className="px-3 py-2 text-right text-xs font-bold uppercase tracking-wider">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Mode</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Reference</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Collected by</th>
                <th className="px-3 py-2 text-left text-xs font-bold uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-t border-background-100">
                  <td className="tnum whitespace-nowrap px-3 py-2 font-mono font-semibold text-primary-700">{r.receiptNumber}</td>
                  <td className="tnum whitespace-nowrap px-3 py-2">{formatDate(r.receivedOn)}</td>
                  <td className="px-3 py-2 font-medium text-foreground-900">{r.memberName}</td>
                  <td className="px-3 py-2 text-foreground-600">{r.lines.map((l) => `${l.groupCode} C${l.cycle}`).join(", ")}</td>
                  <td className="px-3 py-2 text-right font-semibold"><CurrencyDisplay amount={r.total} hideSymbol tone={r.reversedAt ? "danger" : "default"} /></td>
                  <td className="px-3 py-2 capitalize">{paymentModeLabel(r.mode)}</td>
                  <td className="tnum px-3 py-2 text-foreground-500">{r.reference || "—"}</td>
                  <td className="px-3 py-2">{r.collectedBy}</td>
                  <td className="px-3 py-2">{r.reversedAt ? <Badge color="danger">Reversed</Badge> : <Badge color="success">Active</Badge>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold">
                <td className="px-3 py-2.5 text-foreground-600 uppercase text-xs tracking-wider" colSpan={4}>Total ({activeRows.length} active)</td>
                <td className="px-3 py-2.5 text-right font-bold text-base"><CurrencyDisplay amount={data.grandTotal} hideSymbol /></td>
                <td className="px-3 py-2.5" colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </PrintLayout>
      )}
    </div>
  );
}